import * as React from 'react';

import {
  enumDocumentType,
  enumSalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useSetAtom } from 'jotai';
import {
  CopyPlus,
  PlusCircle,
  Printer,
  Trash2,
  Undo2,
  CheckCircle,
} from 'lucide-react';

import { usePrintWithBrowser } from '../../../hooks/usePrintWithBrowser';
import { SalesOrderRecord } from '../../../types/salesOrder';

import { useCanFinishSalesOrder } from './useCanFinishSalesOrder';

import {
  $cloneSalesOrder,
  $updateSalesOrder,
} from '~/modules/trade/_state/salesOrderDetail.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

export type LabelWithIconProps = {
  label: string;
  icon: React.ReactNode;
  className?: string;
};

export const LabelWithIcon = ({
  label,
  icon,
  className,
}: LabelWithIconProps) => (
  <div className={cn('@LabelWithIcon', 'flex items-center gap-2', className)}>
    {icon}
    {label}
  </div>
);

type UseSalesOrderDetailMenuItemsProps = {
  salesOrder: SalesOrderRecord | null;
  setIsLoadDialogOpen: (open: boolean, salesOrderId?: string) => void;
  showVoidButton?: boolean;
  onVoid?: (salesOrderId: string) => void;
  onClose?: (salesOrderId: string) => void;
  onRefetch?: () => void;
  displayListActions?: boolean;
  isMenuOpen?: boolean;
};

/**
 * Hook that provides menu items for sales order detail actions.
 *
 * @param props - Configuration for the menu items
 * @param props.salesOrder - The sales order record
 * @param props.setIsLoadDialogOpen - Function to control load dialog visibility
 * @param props.showVoidButton - Whether to show the void button (default: false)
 * @param props.onRefetch - Function to refetch data after actions
 * @param props.displayListActions - Whether to show list-specific actions like revert (default: false)
 *
 * @example
 * ```tsx
 * const menuItems = useSalesOrderDetailMenuItems({
 *   salesOrder,
 *   setIsLoadDialogOpen,
 *   showVoidButton: true, // Enable void functionality
 *   onRefetch: refetch,
 *   displayListActions: true, // Enable revert functionality
 * });
 * ```
 */
export const useSalesOrderDetailMenuItems = ({
  salesOrder,
  setIsLoadDialogOpen,
  showVoidButton = false,
  onVoid,
  onClose,
  onRefetch,
  displayListActions = false,
  isMenuOpen = true,
}: UseSalesOrderDetailMenuItemsProps) => {
  const toast = useToast();
  const t = useTranslations();
  const cloneSalesOrderMutation = useSetAtom($cloneSalesOrder);
  const updateSalesOrderMutation = useSetAtom($updateSalesOrder);
  const { print } = usePrintWithBrowser();

  // Can close (mark as finished) when status is Open
  const canClose = React.useMemo(
    () =>
      displayListActions && salesOrder?.status === enumSalesOrderStatus.Open,
    [displayListActions, salesOrder?.status],
  );

  const { canFinish, isLoading } = useCanFinishSalesOrder({
    salesOrderId: salesOrder?.id,
    enabled: isMenuOpen && canClose,
  });

  const canCreateLoad = React.useMemo(
    () =>
      salesOrder?.status !== enumSalesOrderStatus.Draft &&
      salesOrder?.status !== enumSalesOrderStatus.Voided,
    [salesOrder],
  );

  const canClone = React.useMemo(
    () => salesOrder?.status !== enumSalesOrderStatus.Draft,
    [salesOrder],
  );

  const canVoid = React.useMemo(
    () =>
      showVoidButton &&
      salesOrder?.status !== enumSalesOrderStatus.Finished &&
      salesOrder?.status !== enumSalesOrderStatus.Voided,
    [salesOrder, showVoidButton],
  );

  // Can revert from Voided or Finished back to Open
  const canRevert = React.useMemo(
    () =>
      displayListActions &&
      (salesOrder?.status === enumSalesOrderStatus.Voided ||
        salesOrder?.status === enumSalesOrderStatus.Finished),
    [salesOrder?.status, displayListActions],
  );

  // Clone action
  const handleClone = React.useCallback(async () => {
    if (!salesOrder?.id) {
      toast.error(t.salesOrderDetail.messages.noSalesOrderIdToClone);
      return;
    }
    try {
      const result = await cloneSalesOrderMutation({
        id: salesOrder.id,
      });
      if (result?.tradeSalesOrderClone?.id) {
        toast.success(t.salesOrderDetail.messages.cloned);
        setTimeout(() => {
          window.open(
            `/trade/orders/${result.tradeSalesOrderClone.id}`,
            '_blank',
          );
        }, 1000);
      } else {
        toast.error(t.salesOrderDetail.messages.cloneFailed);
      }
    } catch (error) {
      toast.error(
        `Failed to clone sales order: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }, [salesOrder, t, cloneSalesOrderMutation, toast]);

  // Create Load action
  const handleCreateLoad = React.useCallback(() => {
    setIsLoadDialogOpen(true, salesOrder?.id);
  }, [setIsLoadDialogOpen, salesOrder?.id]);

  // Print Sales Order action
  const handlePrintSalesOrder = React.useCallback(() => {
    if (!salesOrder?.id) {
      toast.error(t.salesOrderDetail.messages.noSalesOrderIdToPrint);
      return;
    }

    print({
      documents: [
        {
          documentType: enumDocumentType.SalesOrder,
          salesOrderId: salesOrder.id,
        },
      ],
    }).then(() => {
      toast.success(t.salesOrderDetail.actions.printingSalesOrder);
    });

    toast.info(t.salesOrderDetail.actions.printingSalesOrder);
  }, [salesOrder, t, toast, print]);

  // Void Sales Order action
  const handleVoidSalesOrder = React.useCallback(() => {
    if (!salesOrder?.id) {
      toast.error(t.salesOrderDetail.messages.noSalesOrderIdToVoid);
      return;
    }
    onVoid?.(salesOrder.id);
  }, [salesOrder?.id, t, toast, onVoid]);

  // Revert Sales Order action
  const handleRevertSalesOrder = React.useCallback(async () => {
    if (!salesOrder?.id) {
      toast.error(t.salesOrderDetail.messages.noSalesOrderIdToRevert);
      return;
    }
    try {
      await updateSalesOrderMutation({
        id: salesOrder.id,
        status: enumSalesOrderStatus.Open,
      });
      toast.success(t.salesOrderDetail.messages.reverted);
      onRefetch?.();
    } catch (error) {
      toast.error(
        `Failed to revert sales order: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }, [salesOrder?.id, updateSalesOrderMutation, t, toast, onRefetch]);

  // Close Sales Order action
  const handleCloseSalesOrder = React.useCallback(async () => {
    if (!salesOrder?.id) {
      toast.error(t.salesOrderDetail.messages.noSalesOrderIdToClose);
      return;
    }
    onClose?.(salesOrder.id);
  }, [salesOrder?.id, t, toast, onClose]);

  return React.useMemo(
    () => [
      ...(canClone
        ? [
            {
              key: 'clone',
              onAction: handleClone,
              label: (
                <LabelWithIcon
                  label={t.salesOrderDetail.actions.clone}
                  icon={<CopyPlus className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(canCreateLoad
        ? [
            {
              key: 'createLoad',
              onAction: handleCreateLoad,
              label: (
                <LabelWithIcon
                  label={t.salesOrderDetail.actions.createLoad}
                  icon={<PlusCircle className="size-4" />}
                />
              ),
            },
          ]
        : []),
      {
        key: 'printSalesOrder',
        onAction: handlePrintSalesOrder,
        label: (
          <LabelWithIcon
            label={t.salesOrderDetail.actions.printSalesOrder}
            icon={<Printer className="size-4" />}
          />
        ),
      },
      ...(canRevert
        ? [
            {
              key: 'revert',
              onAction: handleRevertSalesOrder,
              label: (
                <LabelWithIcon
                  label={t.salesOrderDetail.actions.revert}
                  icon={<Undo2 className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(canClose
        ? [
            {
              key: 'closeSalesOrder',
              onAction: handleCloseSalesOrder,
              label: (
                <LabelWithIcon
                  label={t.salesOrderDetail.actions.closeSalesOrder}
                  icon={<CheckCircle className="size-4" />}
                />
              ),
              isDisabled: !canFinish,
              isLoading,
            },
          ]
        : []),
      ...(canVoid
        ? [
            {
              key: 'voidSalesOrder',
              onAction: handleVoidSalesOrder,
              label: (
                <LabelWithIcon
                  className="text-failure"
                  label={t.salesOrderDetail.actions.voidSalesOrder}
                  icon={<Trash2 className="size-4" />}
                />
              ),
            },
          ]
        : []),
    ],
    [
      t,
      handleClone,
      handleCreateLoad,
      handlePrintSalesOrder,
      handleVoidSalesOrder,
      handleRevertSalesOrder,
      handleCloseSalesOrder,
      canClone,
      canCreateLoad,
      canVoid,
      canRevert,
      canClose,
    ],
  );
};
